import { transformUrnToWitboostId } from '@agilelab/plugin-wb-builder-common';
import { customAlertApiRef } from '@agilelab/plugin-wb-platform';
import {
  RoleAssignments,
  RoleSubjectEntity,
  rbacRoleEditPermission,
} from '@agilelab/plugin-wb-rbac-common';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useAsyncFn, { AsyncState } from 'react-use/lib/useAsyncFn';
import { rbacApiRef } from '../../..';
import pLimit from 'p-limit';

export interface Filters {
  text?: string;
}

export interface Pagination {
  limit: number;
  offset: number;
}
export interface AssignmentsContextProviderProps {
  children?: ReactNode;
  roleId: string;
}
export interface AssignmentsContextType
  extends AssignmentsContextProviderProps {
  openManageDrawer: boolean;
  setOpenManageDrawer: Dispatch<SetStateAction<boolean>>;

  openEditEnabledScopesDrawer: boolean;
  setEditEnabledScopesDrawer: Dispatch<SetStateAction<boolean>>;

  canEditRolePermission: boolean;

  openConfirmDialog: boolean;
  setOpenConfirmDialog: Dispatch<SetStateAction<boolean>>;

  removeAssignments: (assignmentSubjects: string[]) => Promise<string[]>;

  updateAssignments: (
    userOrGroup: string,
    scopes: {
      urn: string;
      enabled: boolean;
    }[],
    upsert?: boolean,
  ) => Promise<RoleSubjectEntity[]>;

  loadAssignments: () => Promise<RoleAssignments[]>;
  assignmentsLoaded: AsyncState<RoleAssignments[] | undefined>;

  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;

  pagination: Pagination;
  setPagination: Dispatch<SetStateAction<Pagination>>;
  filteredAssignments: RoleAssignments[];
  totalAssignmentsCount: number;

  selectedRemoveItems: RoleAssignments[];
  setSelectedRemoveItems: Dispatch<SetStateAction<RoleAssignments[]>>;

  selectedRow: RoleAssignments | undefined;
  setSelectedRow: Dispatch<SetStateAction<RoleAssignments | undefined>>;
}

export const AssignmentsContext = createContext<AssignmentsContextType>(
  {} as AssignmentsContextType,
);

export const AssignmentsContextProvider = ({
  children,
  roleId,
}: AssignmentsContextProviderProps) => {
  const rbacApi = useApi(rbacApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(customAlertApiRef);

  // check if permission check is enabled
  const permissionsEnabled =
    configApi.getOptionalBoolean('permission.enabled') ?? false;

  const editPermissionAllowed = usePermission({
    permission: rbacRoleEditPermission,
  }).allowed;

  const canEditRolePermission = !permissionsEnabled || editPermissionAllowed;

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const [filters, setFilters] = useState<Filters>({});

  const [pagination, setPagination] = useState<Pagination>({
    limit: 25,
    offset: 0,
  });

  const [openManageDrawer, setOpenManageDrawer] = useState(false);

  const [openEditEnabledScopesDrawer, setEditEnabledScopesDrawer] =
    useState(false);

  const [selectedRemoveItems, setSelectedRemoveItems] = useState<
    RoleAssignments[]
  >([]);

  const [selectedRow, setSelectedRow] = useState<RoleAssignments>();

  const [assignmentsLoaded, loadAssignments] = useAsyncFn(async () => {
    const fetchAssignments = async (): Promise<RoleAssignments[]> => {
      const res: RoleSubjectEntity[] = await rbacApi.getRolesSubjects({
        searchKeyword: filters.text,
        filters: { roleId },
        options: {
          token: (await identityApi.getCredentials()).token,
        },
      });

      const entityMap = new Map<string, Entity>();

      // --- Step 1: Collect unique entity refs to resolve (transform URNs to entity refs)
      const uniqueEntityRefs = new Set<string>();
      const urnToEntityRefMap = new Map<string, string>();

      res.forEach(item => {
        if (item.entityRef && item.entityRef !== '*') {
          const entityRef = item.entityRef.startsWith('urn:')
            ? transformUrnToWitboostId(item.entityRef)
            : item.entityRef;
          if (entityRef) {
            uniqueEntityRefs.add(entityRef);
            urnToEntityRefMap.set(item.entityRef, entityRef);
          }
        }
        if (item.subject) {
          const entityRef = item.subject.startsWith('urn:')
            ? transformUrnToWitboostId(item.subject)
            : item.subject;
          if (entityRef) {
            uniqueEntityRefs.add(entityRef);
            urnToEntityRefMap.set(item.subject, entityRef);
          }
        }
      });

      // --- Step 2: Partition entity refs into batches of 50
      const entityRefsArray = [...uniqueEntityRefs];
      const batchSize = 50;
      const batches: string[][] = [];
      for (let i = 0; i < entityRefsArray.length; i += batchSize) {
        batches.push(entityRefsArray.slice(i, i + batchSize));
      }

      // --- Step 3: Fetch entities in batches
      const token = (await identityApi.getCredentials()).token;
      const limit = pLimit(2); // Limit to 2 concurrent batch requests
      await Promise.all(
        batches.map(batch =>
          limit(async () => {
            try {
              const response = await catalogApi.getEntitiesByRefs(
                { entityRefs: batch },
                { token },
              );
              // Map entities back to their original URNs/refs
              response.items.forEach((entity, index) => {
                if (entity) {
                  const entityRefUsedInCall = batch[index];
                  const stringifiedRef = stringifyEntityRef(entity);

                  // Find all original URNs/refs that map to this entityRef
                  [...urnToEntityRefMap.entries()].forEach(
                    ([originalUrn, mappedRef]) => {
                      if (
                        mappedRef === entityRefUsedInCall ||
                        mappedRef === stringifiedRef
                      ) {
                        entityMap.set(originalUrn, entity);
                      }
                    },
                  );
                }
              });
            } catch (e) {
              // Skip resolution for missing entities in this batch
              return;
            }
          }),
        ),
      );

      // --- Step 3: Transform RoleSubjectEntity[] into RoleAssignments[] grouped by subject
      const grouped = res.reduce((acc, item) => {
        if (!item.subject) return acc;
        const entity = entityMap.get(item.subject);

        if (!acc[item.subject]) {
          acc[item.subject] = {
            roleId: item.roleId!,
            scopes: [],
            userOrGroup: { urn: item.subject, entity: entity! },
          };
        }

        if (item.entityRef) {
          acc[item.subject].scopes.push(
            item.entityRef === '*'
              ? { urn: '*' }
              : {
                  urn: item.entityRef,
                  entity: entityMap.get(item.entityRef),
                  enabled: item.enabled,
                },
          );
        }

        return acc;
      }, {} as Record<string, RoleAssignments>);

      return Object.values(grouped);
    };

    return fetchAssignments();
  }, [filters]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const filteredAssignments = useMemo(() => {
    const assignments = assignmentsLoaded.value || [];
    if (!filters.text) {
      return assignments;
    }
    const searchLower = filters.text.toLowerCase();
    return assignments.filter(assignment => {
      const displayName =
        (assignment.userOrGroup?.entity?.spec?.profile as any)?.displayName ??
        assignment.userOrGroup?.entity?.metadata?.name ??
        assignment.userOrGroup?.urn;
      return displayName?.toLowerCase().includes(searchLower);
    });
  }, [assignmentsLoaded.value, filters.text]);

  const removeAssignments = async (assignmentSubjects: string[]) => {
    try {
      const token = (await identityApi.getCredentials()).token;

      const results = await Promise.allSettled(
        assignmentSubjects.map(subject =>
          rbacApi.deleteRolesSubjects({
            filters: { roleId, subject },
            options: { token },
          }),
        ),
      );

      const failed = results.filter(result => result.status === 'rejected');

      if (failed.length > 0) {
        failed.forEach(failure => {
          const customError = (failure as any).reason.body.error;
          alertApi.post({
            message: `${customError.message}: ${customError.errorMessage}`,
            severity: 'error',
          });
        });
      } else {
        alertApi.post({
          message: `Assignments successfully removed`,
          severity: 'success',
        });
      }

      setSelectedRemoveItems([]);
      loadAssignments();
      return [];
    } catch (e) {
      setOpenManageDrawer(false);
      alertApi.post({
        message: e.message || 'An error occurred',
        severity: 'error',
      });
      return [];
    } finally {
      setSelectedRow(undefined);
    }
  };

  const updateAssignments = async (
    userOrGroup: string,
    scopes: { urn: string; enabled: boolean }[],
  ) => {
    try {
      const token = (await identityApi.getCredentials()).token;

      const previousScopeMap = new Map(
        (selectedRow?.scopes || []).map(scope => [scope.urn, scope.enabled]),
      );

      const changedScopes = scopes.filter(scope => {
        return previousScopeMap.get(scope.urn) !== scope.enabled;
      });

      const rolesSubjects = changedScopes.map(scope => ({
        subject: userOrGroup,
        roleId: roleId,
        entityRef: scope.urn,
        enabled: scope.enabled ?? true,
      }));

      const results = await rbacApi.addRolesSubjects({
        rolesSubjects,
        options: { token, upsert: true },
      });
      setEditEnabledScopesDrawer(false);
      loadAssignments();

      alertApi.post({
        message: `Successfully updates ${results.length} assignment(s).`,
        severity: results.length ? 'success' : 'warning',
      });

      return results;
    } catch (e) {
      setEditEnabledScopesDrawer(false);
      alertApi.post({
        message: e.message || 'An error occurred while adding assignments.',
        severity: 'error',
      });
      return [];
    } finally {
      setSelectedRow(undefined);
    }
  };

  return (
    <AssignmentsContext.Provider
      value={{
        roleId,

        loadAssignments,
        assignmentsLoaded,

        canEditRolePermission,

        openConfirmDialog,
        setOpenConfirmDialog,

        openManageDrawer,
        setOpenManageDrawer,

        openEditEnabledScopesDrawer,
        setEditEnabledScopesDrawer,

        removeAssignments,

        filters,
        setFilters,

        pagination,
        setPagination,
        filteredAssignments,
        totalAssignmentsCount: filteredAssignments.length,

        selectedRemoveItems,
        setSelectedRemoveItems,

        selectedRow,
        setSelectedRow,

        updateAssignments,
      }}
    >
      {children}
    </AssignmentsContext.Provider>
  );
};

export const useAssignmentsContext = () => {
  const context = useContext(AssignmentsContext);
  if (!context) {
    throw new Error(
      'useAssignmentsContext must be used within a AssignmentsContextProvider',
    );
  }
  return context as AssignmentsContextType;
};
